import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClientShell } from '../components/ClientShell';
import { DaySelector } from '../components/DaySelector';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ExerciseDetailDialog } from '../components/ExerciseDetailDialog';
import { ArrowLeft, CheckCircle, Circle, Save, Clock, Info, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ClientPlanView = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [exercisesById, setExercisesById] = useState({});
  const [previewExercise, setPreviewExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(1);
  const [logging, setLogging] = useState(false);
  
  const [workoutLog, setWorkoutLog] = useState({
    exercises: [],
    overall_notes: '',
    duration_minutes: ''
  });

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const [plansRes, exercisesRes] = await Promise.all([
        axios.get(`${API_URL}/api/client/my-plans`, { withCredentials: true }),
        axios.get(`${API_URL}/api/exercises`, { withCredentials: true })
      ]);
      const byId = {};
      exercisesRes.data.forEach(ex => { byId[ex.id] = ex; });
      setExercisesById(byId);

      const foundPlan = plansRes.data.find(p => p.id === planId);
      if (foundPlan) {
        setPlan(foundPlan);
        initializeWorkoutLog(foundPlan, selectedDay);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializeWorkoutLog = (planData, day) => {
    const dayExercises = planData.exercises.filter(ex => ex.day_number === day);
    setWorkoutLog({
      exercises: dayExercises.map(ex => ({
        exercise_id: ex.exercise_id,
        exercise_name: ex.exercise_name,
        completed: false,
        actual_sets: ex.sets,
        actual_reps: ex.reps,
        actual_weight: ex.weight_kg,
        notes: ''
      })),
      overall_notes: '',
      duration_minutes: ''
    });
  };

  const handleDayChange = (day) => {
    setSelectedDay(day);
    initializeWorkoutLog(plan, day);
  };

  const toggleExerciseComplete = (index) => {
    const newExercises = [...workoutLog.exercises];
    newExercises[index].completed = !newExercises[index].completed;
    setWorkoutLog({ ...workoutLog, exercises: newExercises });
  };

  const updateExerciseLog = (index, field, value) => {
    const newExercises = [...workoutLog.exercises];
    newExercises[index][field] = value;
    setWorkoutLog({ ...workoutLog, exercises: newExercises });
  };

  const handleSaveWorkout = async () => {
    setLogging(true);
    try {
      await axios.post(
        `${API_URL}/api/workout-logs`,
        {
          plan_id: planId,
          day_number: selectedDay,
          log_date: localISODate(),
          exercises: workoutLog.exercises,
          overall_notes: workoutLog.overall_notes,
          duration_minutes: workoutLog.duration_minutes ? parseInt(workoutLog.duration_minutes) : null
        },
        { withCredentials: true }
      );
      toast.success('Trening zalogowany!');
      navigate('/client-dashboard');
    } catch (error) {
      console.error('Error logging workout:', error);
      toast.error('Błąd logowania treningu');
    } finally {
      setLogging(false);
    }
  };

  if (loading || !plan) {
    return (
      <ClientShell>
        <div className="py-24 text-center text-[var(--text-muted)]">{loading ? 'Ładowanie...' : 'Plan nie znaleziony'}</div>
      </ClientShell>
    );
  }

  const dayExercises = plan.exercises.filter(ex => ex.day_number === selectedDay);
  const completedCount = workoutLog.exercises.filter(ex => ex.completed).length;

  // In the page on wider screens, pinned above the tab bar on phones.
  const startButton = (className) => dayExercises.length > 0 && (
    <Button
      onClick={() => navigate(`/client-plan/${planId}/trening/${selectedDay}`)}
      className={`w-full bg-[#C9A84C] hover:bg-[#B8973B] text-[#0A0A0A] rounded-full font-bold ${className}`}
      data-testid="start-workout-player"
    >
      <Play className="w-6 h-6 mr-2" />
      Rozpocznij trening {selectedDay}
    </Button>
  );

  return (
    <ClientShell action={startButton('py-6 text-base')}>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-12" data-testid="client-plan-view">
        <button onClick={() => navigate('/client-panel')}
          className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] mb-4 sm:mb-6 py-2">
          <ArrowLeft className="w-4 h-4" /> Powrót do panelu
        </button>
        <div className="mb-8 sm:mb-12">
          <h1 className="display-text text-4xl sm:text-5xl text-white mb-2 break-words">{plan.plan_name}</h1>
          <p className="body-text text-[#A1A1A1] text-lg">
            Częstotliwość: {plan.frequency} dni/tydzień
          </p>
        </div>

        {/* Day Selector: generator plans repeat days within the week (A-B-A), so show only the distinct days */}
        <DaySelector
          selected={selectedDay}
          onSelect={handleDayChange}
          days={[...Array(plan.source === 'generator' && plan.exercises.length
            ? Math.max(...plan.exercises.map(ex => ex.day_number)) : plan.frequency)].map((_, i) => {
            const dayExercises = plan.exercises.filter(ex => ex.day_number === i + 1);
            return { day: i + 1, label: dayExercises.find(ex => ex.day_label)?.day_label, count: dayExercises.length };
          })}
        />

        <div className="hidden sm:block">{startButton('mb-8 py-7 text-lg')}</div>
        {dayExercises.length > 0 && (
          <Button
            onClick={() => navigate(`/client-plan/${planId}/dziennik/${selectedDay}`)}
            variant="ghost"
            className="w-full sm:-mt-5 mb-8 border border-[#2A2A2A] rounded-full py-6 text-sm"
            data-testid="open-journal-plan"
          >
            Wpisz trening bez timera (dziennik)
          </Button>
        )}

        {/* Progress */}
        <div className="card-surface rounded-3xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="label-text text-[#A1A1A1]">POSTĘP TRENINGU</span>
              <div className="text-2xl text-white mt-2">
                {completedCount} / {dayExercises.length} ćwiczeń
              </div>
            </div>
            <div className="w-24 h-24 rounded-full border-4 border-[#2A2A2A] flex items-center justify-center">
              <span className="text-2xl text-[#C9A84C] font-bold">
                {dayExercises.length > 0 ? Math.round((completedCount / dayExercises.length) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Exercises */}
        <div className="space-y-4 mb-8">
          {dayExercises.map((planEx, index) => {
            const logEx = workoutLog.exercises[index];
            if (!logEx) return null;
            
            return (
              <div
                key={index}
                className={`card-surface rounded-3xl p-6 border transition-all ${
                  logEx.completed ? 'border-[#C9A84C]/50 bg-[#C9A84C]/5' : 'border-[#2A2A2A]'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    <button
                      onClick={() => toggleExerciseComplete(index)}
                      className="mt-1"
                    >
                      {logEx.completed ? (
                        <CheckCircle className="w-6 h-6 text-[#C9A84C]" />
                      ) : (
                        <Circle className="w-6 h-6 text-[#A1A1A1]" />
                      )}
                    </button>
                    <div className="flex-1">
                      {(planEx.superset_group || planEx.circuit_group) && (
                        <p className="text-xs font-semibold text-[#C9A84C] mb-1">
                          {planEx.superset_group ? `Super-seria ${planEx.superset_group}` : planEx.circuit_group}
                        </p>
                      )}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="heading-text text-xl text-white">{planEx.exercise_name}</h3>
                        {exercisesById[planEx.exercise_id] && (
                          <button
                            onClick={() => setPreviewExercise(exercisesById[planEx.exercise_id])}
                            className="flex items-center gap-1 text-[#C9A84C] text-xs shrink-0 px-3 py-1.5 rounded-full border border-[#C9A84C]/30 hover:bg-[#C9A84C]/10"
                            data-testid={`exercise-instructions-${index}`}
                          >
                            <Info className="w-3.5 h-3.5" />
                            Jak wykonać?
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-[#A1A1A1] mb-4">
                        <span>Serie: {planEx.sets}</span>
                        <span>Powtórzenia: {planEx.reps}</span>
                        {planEx.tempo && <span>Tempo: {planEx.tempo}</span>}
                        {planEx.rir != null && <span>RIR: {planEx.rir}</span>}
                        {planEx.rpe != null && <span>RPE: {planEx.rpe}</span>}
                        {planEx.rest_seconds > 0 && <span>Przerwa: {planEx.rest_seconds}s</span>}
                        {planEx.weight_kg && <span>Ciężar: {planEx.weight_kg}kg</span>}
                      </div>

                      {planEx.notes && (
                        <div className="mb-4 p-3 rounded-2xl bg-[#C9A84C]/10 border border-[#C9A84C]/20">
                          <p className="text-xs text-[#C9A84C] mb-1 label-text">NOTATKA OD TRENERA</p>
                          <p className="text-sm text-white">{planEx.notes}</p>
                        </div>
                      )}

                      {/* Actual values */}
                      <div className="grid grid-cols-[1fr_1.35fr_1fr] sm:grid-cols-3 gap-3 mb-3 items-end">
                        <div>
                          <label className="label-text !tracking-normal whitespace-nowrap text-[#A1A1A1] text-[0.6rem] mb-1 block">SERIE</label>
                          <Input
                            type="number"
                            value={logEx.actual_sets || ''}
                            onChange={(e) => updateExerciseLog(index, 'actual_sets', parseInt(e.target.value) || null)}
                            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="label-text !tracking-normal whitespace-nowrap text-[#A1A1A1] text-[0.6rem] mb-1 block">POWTÓRZENIA</label>
                          <Input
                            value={logEx.actual_reps || ''}
                            onChange={(e) => updateExerciseLog(index, 'actual_reps', e.target.value)}
                            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="label-text !tracking-normal whitespace-nowrap text-[#A1A1A1] text-[0.6rem] mb-1 block">CIĘŻAR (KG)</label>
                          <Input
                            type="number"
                            step="0.5"
                            value={logEx.actual_weight || ''}
                            onChange={(e) => updateExerciseLog(index, 'actual_weight', parseFloat(e.target.value) || null)}
                            className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white h-8 text-sm"
                          />
                        </div>
                      </div>

                      <Textarea
                        placeholder="Notatki z ćwiczenia..."
                        value={logEx.notes || ''}
                        onChange={(e) => updateExerciseLog(index, 'notes', e.target.value)}
                        className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white text-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Overall Notes & Duration */}
        <div className="card-surface rounded-3xl p-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-3">
              <label className="label-text text-[#A1A1A1] mb-2 block">NOTATKI OGÓLNE</label>
              <Textarea
                placeholder="Jak czułeś się podczas treningu?"
                value={workoutLog.overall_notes}
                onChange={(e) => setWorkoutLog({ ...workoutLog, overall_notes: e.target.value })}
                className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                rows={3}
              />
            </div>
            <div>
              <label className="label-text text-[#A1A1A1] mb-2 block">CZAS (MIN)</label>
              <Input
                type="number"
                placeholder="60"
                value={workoutLog.duration_minutes}
                onChange={(e) => setWorkoutLog({ ...workoutLog, duration_minutes: e.target.value })}
                className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSaveWorkout}
            disabled={logging || completedCount === 0}
            className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full px-12 py-6 text-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            {logging ? 'Zapisywanie...' : 'Zapisz trening'}
          </Button>
        </div>
      </main>

      <ExerciseDetailDialog
        exercise={previewExercise}
        open={!!previewExercise}
        onOpenChange={(open) => !open && setPreviewExercise(null)}
      />
    </ClientShell>
  );
};
